import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Pagination,
  ApiVisa,
  InventoryItemContainerModel,
  InventoryItemService
} from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, tap, filter, switchMap, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { AppConfig } from '../../shared-utilities/visa';

@Component({
  selector: 'blg-select-inv-item-drop-down',
  templateUrl: './select-inv-item-drop-down.component.html',
  styleUrls: ['./select-inv-item-drop-down.component.scss']
})
export class SelectInvItemDropDownComponent implements OnInit, OnDestroy {

  @Input() inventory: FormControl;
  apiVisa = AppConfig.apiVisa;
  @Input() multiple = false;

  @Output() inventoryChange = new EventEmitter<FormControl>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<InventoryItemContainerModel[]> = new ReplaySubject<InventoryItemContainerModel[]>(1);
  inventoryList: InventoryItemContainerModel[] = [];
  selectedInventory = new InventoryItemContainerModel();

  protected _onDestroy = new Subject<void>();

  constructor( private invService: InventoryItemService) { }

  ngOnInit() {
    if (this.inventory.value) {
      this.invService.getByGuid(this.inventory.value, this.apiVisa).pipe(
        takeUntil(this._onDestroy)
      ).subscribe( resolved => {
        this.inventoryList = [resolved.data];
        this.filteredOptions$.next([resolved.data]);
        this.onSelect(resolved.data.bl_inv_mst_item_hdr.guid.toString());
      });
    }
    this.serverSideFiltering.valueChanges.pipe(
        filter(searchText => searchText.length > 3),
        tap(() => this.serverSideSearching = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchText => this.searchBranch(searchText))
      ).subscribe( resolved => {
        this.serverSideSearching = false;
        if (this.selectedInventory) {
          this.filteredOptions$.next([...resolved, this.selectedInventory]);
          this.inventoryList = [...resolved, this.selectedInventory];
        } else {
          this.filteredOptions$.next(resolved);
          this.inventoryList = resolved;
        }
      }, error => {
        this.serverSideSearching = false;
        console.error(error);
      });
  }

  /**
   * Server-side inventory-item typeahead.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_inv_mst_item_hdr hdr
   *   WHERE hdr.status != 'DELETED'
   *     AND (hdr.code ILIKE '%x%' OR hdr.name ILIKE '%x%')
   *   ORDER BY hdr.name, hdr.guid
   *
   * GET inv-items/query -> InventoryItemUow.generateWhereClauseByCriteria applies
   * `search_word` as exactly `hdr.code ILIKE ... OR hdr.name ILIKE ...`, and
   * defaults to `status != 'DELETED'` when no status is sent. This is a
   * like-for-like replacement of the old WHERE clause.
   *
   * One request now instead of one subquery plus one call per hit. The old per-hit
   * call passed `hdr_guids`, which InventoryItemQueryCriteria does not define
   * (its guid filter is `guids`), so it matched nothing in particular -- another
   * reason the single query is the better shape.
   */
  searchBranch(searchText: string) {
    return this.invService.getByCriteria(
      new Pagination(0, 100, [
        { columnName: 'search_word', operator: '=', value: (searchText || '').trim() }
      ]),
      this.apiVisa
    ).pipe(
      map(response => response.data)
    );
  }

  onSelect(branchGuid: string) {
    this.inventory.patchValue(branchGuid);
    this.selectedInventory = this.inventoryList.find( inv => inv.bl_inv_mst_item_hdr.guid === branchGuid);
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
